import type { ConnectionItemProps } from "@wayflyer/flyui";
import { IconBank24Line, IconEmptyPage24Line } from "@wayflyer/flyui-icons/24/line";
import type { BankingSettings } from "bankconnect";
import { useBankConnectionLogoResolvers, useBankConnectionLogos, useBankConnectWithBasiq } from "bankconnect";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";
import type { FundingApplicationSchemas } from "api-types";

import { BankLogoDisplay } from "../../../components";
import { COMMON_MESSAGES } from "../../../components/common-messages";
import { NAVIGATION_PATHS } from "../../../components/constants";
import { useBankConnectProps } from "../../../queries/bank-connect/queries";

export const useBankConnectionsAsConnectionItems = (
  customerDomain: string,
  onConnectionRemove: () => void,
  bankingSources: BankingSettings,
  bankingStage?: FundingApplicationSchemas.BankingStageDataScreen | null
): ConnectionItemProps[] => {
  const { navigate } = useSpaNavigate("FundingApp");
  const intl = useIntl();
  const {
    data: { bankConnectProps },
  } = useBankConnectProps(customerDomain);
  const {
    plaidConnectProps: { getInstitutionLogoPath },
    basiqConnectProps,
  } = bankConnectProps;
  const { basiqClient } = useBankConnectWithBasiq(basiqConnectProps);

  const logoResolvers = useBankConnectionLogoResolvers({ getInstitutionLogoPath, basiqClient });

  const { logoLoadingStates } = useBankConnectionLogos({
    bankConnections: bankingSources.bankingDataSources.length ? bankingSources.bankingDataSources : [],
    resolvers: logoResolvers,
  });

  if (!bankingStage) {
    // If no banking stage, early exit
    return [];
  }
  const requireConnection = bankingStage.requireConnection === true;
  const hasBankConnections = !!bankingSources.bankingDataSources?.length;
  const hasBankStatementGroups = !!bankingSources.bankingStatementsGroups?.length;
  const bankCompleted = hasBankConnections || (!requireConnection && hasBankStatementGroups);

  const completedBankingDataSources: ConnectionItemProps[] =
    bankingSources.bankingDataSources?.map((dataSource) => {
      const connectedAccounts = dataSource.accounts.filter((account) => account.pull_enabled);
      return {
        testId: `banking-data-${dataSource.institutionID}`,
        label: dataSource.institutionName,
        logo: <BankLogoDisplay logoState={logoLoadingStates[dataSource.institutionID]} />,
        badgeStatus: "positive",
        badgeText: intl.formatMessage(COMMON_MESSAGES.connected),
        subtitle: connectedAccounts.length > 0 && (
          <ul>
            {connectedAccounts.map((account) => (
              <li key={account.id}>
                {intl.formatMessage(
                  { id: "funding-application: account ending", defaultMessage: "Account ending {mask}" },
                  { mask: account.mask }
                )}
              </li>
            ))}
          </ul>
        ),
      };
    }) ?? [];

  const completedBankStatementGroups: ConnectionItemProps[] =
    bankingSources.bankingStatementsGroups?.map((group, index) => ({
      testId: `banking-document-${group.name}-${index}`,
      label: `${group.name}`,
      logo: <IconEmptyPage24Line />,
      badgeStatus: "positive",
      badgeText: intl.formatMessage(COMMON_MESSAGES.uploaded),
      subtitle: group.previouslyUploadedStatements && (
        <ul>
          {group.previouslyUploadedStatements?.map((statement, index) => (
            <li key={statement.fileName + index}>{statement.fileName}</li>
          ))}
        </ul>
      ),
    })) ?? [];

  const connectBankingItem: ConnectionItemProps = {
    testId: "connect-banking",
    logo: <IconBank24Line />,
    label: intl.formatMessage({ id: "funding-application: bank accounts label", defaultMessage: "Bank accounts" }),
    actions: [
      {
        critical: true,
        content: intl.formatMessage({ id: "funding-application: remove platform label", defaultMessage: "Remove" }),
        onAction: onConnectionRemove,
      },
      {
        content: intl.formatMessage(COMMON_MESSAGES.connect),
        onAction: () => navigate(NAVIGATION_PATHS.BANKING),
      },
    ],
  };

  if (bankCompleted) {
    return [...completedBankingDataSources, ...completedBankStatementGroups];
  }

  if (requireConnection && hasBankStatementGroups) {
    return [connectBankingItem, ...completedBankStatementGroups];
  }

  return [connectBankingItem];
};
